import React, { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";

import "./RunBatchProcessing.scss";
import Button from "../../../../components/button/Button";
import DatePicker from "../../../../components/datepicker/DatePicker";
import LoadingSpinner from "../../../../components/loading-spinner/LoadingSpinner";
import RadioButton from "../../../../components/radio-button/RadioButton";
import { Dialog } from "../../../../components/dialog/Dialog";
import { Detection, Warning } from "../../../../components/svg/svgImageList";
import Svg from "../../../../components/svg/Svg";
import useThemeService from "../../../../services/theme-service/ThemeService";
import { translationService } from "../../../../services/translation/TranslationService";
import {
  RunVideoAnalysis,
  RunVideoAnalysisFromAssignedProject,
} from "../../../../services/authorization/Permissions";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import { currentUserService } from "../../../../services/user/CurrentUserStorage";
import { batchHighlightDetectionService } from "../../../../services/batch-processing/BatchHighlightDetectionService";
import InformationBeforeAction from "../../../../components/data-components/InformationBeforeAction";
import { getLocalPrice } from "../../../../helpers/MathHelpers";
import Checkbox from "../../../../components/checkbox/Checkbox";
import { RunVideoAnalysisServiceModel } from "../../../../services/inspection/RunVideoAnalysisService";
import { OrganizationSubscriptionPlansStorageModel } from "../../../../services/organization-subscription-plans/OrganizationSubscriptionPlansStorage";
import { SubscriptionPlanUsageStorageModel } from "../../../../services/organization-subscription-plans/SubscriptionPlanUsageStorage";
import { api } from "../../../../services/api/ApiService";
import useModal from "../../../../components/dialog/useModal";
import BatchProcessingSummary from "./BatchProcessingSummary";
import { BatchProcessingSegmentationOption } from "../../../../generated/VideoSegmentation";
import { BatchProcessingInspectionOption } from "../../../../generated/VideoAnalysis";

interface RunBatchHighlightDetection {
  selectedSewerObjects: string[];
}

export default observer(function RunBatchHighlightDetection(props: RunBatchHighlightDetection) {
  const { themeClass } = useThemeService();
  const { toggleModal, isVisible } = useModal();

  const [subscriptionPlanUsageStorage] = useState(() =>
    SubscriptionPlanUsageStorageModel.create({ autoLoad: false }, { api })
  );

  const [runVideoAnalysisService] = useState(() => RunVideoAnalysisServiceModel.create({}, {}));

  const [organizationSubscriptionPlansStorage] = useState(() =>
    OrganizationSubscriptionPlansStorageModel.create({ autoLoad: false }, { api })
  );

  const isRunBatchHighlightDetectionDisabled = useMemo(
    () =>
      !permissionsService.userHasOneOfThePermissions([
        RunVideoAnalysis,
        RunVideoAnalysisFromAssignedProject,
      ]) ||
      props.selectedSewerObjects.length <= 0 ||
      !currentUserService.currentUser.currentOrganization.hasPaidSubscription,
    [props.selectedSewerObjects.length]
  );

  const onRunAnalysisClick = useCallback(async () => {
    const videoCallsUsage =
      await subscriptionPlanUsageStorage.getOrganizationVideoAnalysisCallsUsage();

    if (
      runVideoAnalysisService.VACallsStillAvailableAndPopupWasNotDismissed(
        videoCallsUsage,
        batchHighlightDetectionService.totalCalls
      )
    ) {
      await batchHighlightDetectionService.triggerBatchDetection();
      return;
    }

    if (
      runVideoAnalysisService.VACallsStillAvailableAndPopupWasPreviouslyDismissed(
        videoCallsUsage,
        batchHighlightDetectionService.totalCalls
      )
    ) {
      runVideoAnalysisService.clearVACallsPreferencesFromStorage();
      await batchHighlightDetectionService.triggerBatchDetection();
      return;
    }

    if (
      runVideoAnalysisService.VACallsNotAvailableAndPopupWasNotDismissed(
        videoCallsUsage,
        batchHighlightDetectionService.totalCalls
      )
    ) {
      await organizationSubscriptionPlansStorage.getExtraVideoAnalysisUnitPrice();
      toggleModal();
      return;
    }

    if (
      runVideoAnalysisService.VACallsNotAvailableAndPopupWasDismissedInThisBillingCycle(
        videoCallsUsage,
        batchHighlightDetectionService.totalCalls
      )
    ) {
      await batchHighlightDetectionService.triggerBatchDetection();
      return;
    }

    if (
      runVideoAnalysisService.VACallsNotAvailableAndPopupWasDismissedInPreviousBillingCycle(
        videoCallsUsage,
        batchHighlightDetectionService.totalCalls
      )
    ) {
      runVideoAnalysisService.clearVACallsPreferencesFromStorage();
      await organizationSubscriptionPlansStorage.getExtraVideoAnalysisUnitPrice();
      toggleModal();
      return;
    }
  }, [
    organizationSubscriptionPlansStorage,
    runVideoAnalysisService,
    subscriptionPlanUsageStorage,
    toggleModal,
  ]);

  const handleVACallsExceededConfirmationAction = useCallback(async () => {
    if (runVideoAnalysisService.isDoNotShowCallsExceededWarningMessageAgainSelected) {
      runVideoAnalysisService.disableVACallsExceededWarningMessageForOrganization();
    }
    toggleModal();
    await batchHighlightDetectionService.triggerBatchDetection();
  }, [runVideoAnalysisService, toggleModal]);

  return (
    <>
      <Button
        id="run-batch-highlight-detection"
        onClick={() => batchHighlightDetectionService.showRunBatchModal(props.selectedSewerObjects)}
        disabled={isRunBatchHighlightDetectionDisabled}
        icon={{
          svg: Detection,
          textKey: "RunHighlightDetectionButton.run",
          textClass: "actions-header-button-label",
          className: themeClass(["icon-video"]),
        }}
      />
      <Dialog
        titleKey="damages.RunBatchHighlightDetection.title"
        titleContext={{ sewerObjectsCount: props.selectedSewerObjects.length }}
        sidePosition
        isVisible={batchHighlightDetectionService.displayModal}
        onHide={batchHighlightDetectionService.hideRunBatchModal}
        buttonOk={{
          id: "run-video-analysis",
          textKey: "VideoAnalysis.run-both",
          disabled: batchHighlightDetectionService.disabledRunAnalysis,
          action: onRunAnalysisClick,
        }}
        buttonCancel={{
          textKey: "general.cancel",
          action: batchHighlightDetectionService.hideRunBatchModal,
        }}
      >
        <section className="run-batch-analysis-container">
          <h4>
            {translationService.translate("damages.RunBatchVideoAnalysis.message", {
              sewerObjectsCount: props.selectedSewerObjects.length,
            })}
          </h4>
          <RadioButton
            labelKey="damages.RunBatchVideoAnalysis.option.relevant-inspections"
            name="batch-option"
            onChange={(e) =>
              batchHighlightDetectionService.handleRunOptionChanged(Number(e.target.value))
            }
            checked={
              batchHighlightDetectionService.runAnalysisOption ===
              BatchProcessingInspectionOption.RelevantInspections
            }
            value={String(BatchProcessingInspectionOption.RelevantInspections)}
          />
          <RadioButton
            labelKey="damages.RunBatchVideoAnalysis.option.starting-with-date.label"
            name="batch-option"
            onChange={(e) =>
              batchHighlightDetectionService.handleRunOptionChanged(Number(e.target.value))
            }
            checked={
              batchHighlightDetectionService.runAnalysisOption ===
              BatchProcessingInspectionOption.StartingWithDate
            }
            value={String(BatchProcessingInspectionOption.StartingWithDate)}
          >
            <DatePicker
              className="starting-with-date"
              value={batchHighlightDetectionService.startingWithDate}
              onChange={(date) =>
                batchHighlightDetectionService.changeStartingWithDate(date as Date | null)
              }
            />
          </RadioButton>
          <RadioButton
            labelKey="damages.RunBatchVideoAnalysis.option.all-inspections.label"
            name="batch-option"
            onChange={(e) =>
              batchHighlightDetectionService.handleRunOptionChanged(Number(e.target.value))
            }
            checked={
              batchHighlightDetectionService.runAnalysisOption ===
              BatchProcessingInspectionOption.AllInspections
            }
            value={String(BatchProcessingInspectionOption.AllInspections)}
          />
        </section>
        <section className="run-batch-analysis-container">
          <RadioButton
            labelKey="damages.RunBatchHighlightDetection.exclude-done-hd.label"
            onChange={(e) =>
              batchHighlightDetectionService.handleSegmentationOptionChanged(Number(e.target.value))
            }
            checked={
              batchHighlightDetectionService.segmentationProcessingOption ===
              BatchProcessingSegmentationOption.ExcludeInspectionsWithExistingSegmentation
            }
            value={String(
              BatchProcessingSegmentationOption.ExcludeInspectionsWithExistingSegmentation
            )}
          />
          <RadioButton
            labelKey="damages.RunBatchHighlightDetection.redo-existing-hd.label"
            onChange={(e) =>
              batchHighlightDetectionService.handleSegmentationOptionChanged(Number(e.target.value))
            }
            checked={
              batchHighlightDetectionService.segmentationProcessingOption ===
              BatchProcessingSegmentationOption.RedoExistingSegmentations
            }
            value={String(BatchProcessingSegmentationOption.RedoExistingSegmentations)}
          />
          <RadioButton
            labelKey="damages.RunBatchHighlightDetection.redo-existing-va-hd.label"
            onChange={(e) =>
              batchHighlightDetectionService.handleSegmentationOptionChanged(Number(e.target.value))
            }
            checked={
              batchHighlightDetectionService.segmentationProcessingOption ===
              BatchProcessingSegmentationOption.RedoExistingVAsAndSegmentations
            }
            value={String(BatchProcessingSegmentationOption.RedoExistingVAsAndSegmentations)}
          />
        </section>
        <section className="run-batch-analysis-container">
          {batchHighlightDetectionService.isLoading &&
            batchHighlightDetectionService.isStartingWithDateNeeded && (
              <div className="warning">
                <Svg svg={Warning} />
                <p>
                  {translationService.translate(
                    "damages.BatchProcessing.summary.starting-date.required"
                  )}
                </p>
              </div>
            )}
          {batchHighlightDetectionService.isLoading &&
            !batchHighlightDetectionService.isStartingWithDateNeeded && (
              <LoadingSpinner spinnerOnly />
            )}
          {!batchHighlightDetectionService.isLoading && (
            <BatchProcessingSummary
              sewerObjectsCount={batchHighlightDetectionService.batchSummary!.objectsCount}
              inspectionsCount={batchHighlightDetectionService.batchSummary!.inspectionsCount}
              analysingInspectionsCount={
                batchHighlightDetectionService.batchSummary!.analysingInspectionsCount
              }
              videoAnalysisCount={batchHighlightDetectionService.batchSummary!.videoAnalysisCount}
              videoSegmentationCount={
                batchHighlightDetectionService.batchSummary!.segmentationsCount
              }
            />
          )}
        </section>
        <section className="run-batch-analysis-container">
          {translationService.translate("damages.RunBatchHighlightDetection.mention")}
        </section>
      </Dialog>
      <InformationBeforeAction
        isVisible={isVisible}
        titleKey="damages.RunBatchVideoAnalysis.exceeded-va-calls.title"
        buttonOk={{
          id: "run-va-ok",
          textKey: "VideoAnalysis.run-both",
          action: handleVACallsExceededConfirmationAction,
        }}
        toggleModal={toggleModal}
      >
        <>
          <div>
            {translationService.translate("damages.RunBatchVideoAnalysis.exceeded-calls.text")}
            <Link to={"/subscription/plans"}>
              {translationService.translate("damages.RunBatchVideoAnalysis.exceeded-calls.plan")}
            </Link>
            {translationService.translate("damages.RunBatchVideoAnalysis.exceeded-calls.price", {
              extraVACallPrice: getLocalPrice(
                organizationSubscriptionPlansStorage.extraVideoAnalysisUnitPrice
              ),
            })}
          </div>
          <div>
            {translationService.translate(
              "damages.RunBatchVideoAnalysis.exceeded-va-calls.total-extra-calls",
              {
                numberOfCalls: subscriptionPlanUsageStorage.getNumberOfExtraCallsAboveLimit(
                  batchHighlightDetectionService.totalCalls
                ),
              }
            )}
          </div>
          <div>
            <span className="bolded-text">
              {translationService.translate(
                "damages.RunBatchVideoAnalysis.exceeded-va-calls.total-cost",
                {
                  totalCost: getLocalPrice(
                    organizationSubscriptionPlansStorage.extraVideoAnalysisUnitPrice *
                      subscriptionPlanUsageStorage.getNumberOfExtraCallsAboveLimit(
                        batchHighlightDetectionService.totalCalls
                      )
                  ),
                }
              )}
            </span>
          </div>
        </>
        <Checkbox
          className="run-ai-warning-message-checkbox"
          checked={runVideoAnalysisService.isDoNotShowCallsExceededWarningMessageAgainSelected}
          onChange={runVideoAnalysisService.toggleIsDoNotShowThisPopupAgainSelected}
          labelKey="general.dont-show-this-message-again"
        />
      </InformationBeforeAction>
    </>
  );
});
